import { useEffect, useMemo, useState } from 'react'
import Image from 'next/dist/client/image'
import { useTranslation } from 'hooks/useTranslation'
import { Box } from 'components/Box'
import { Button } from 'components/Button'
import { Footer } from 'components/Footer'
import { Header } from 'components/Header'
import { Layout } from 'components/Layout'
import { Link } from 'components/Link'
import { Stack } from 'components/Stack'
import { Text } from 'components/Text'
import { InfluencerReview } from './components/InfluencerReview'
import { Review } from './components/Review'
import { ReviewCard } from './components/ReviewCard'
import { Trustpilot } from './components/Trustpilot'
import {
  BenefitsImage,
  BenefitsSection,
  Claim,
  Column,
  HeroSection,
  ImageContainer,
  ImagineImage,
  ImagineSection,
  ImagineSectionContent,
  Masonry,
  ReviewHero,
  ReviewPattern,
  ReviewsRoot,
  TrustpilotContent,
} from './Reviews.styles'

import isologo from '/public/assets/isologo.svg'
import stepFeed from '/public/assets/illustration-feed.svg'
import benefitHeart from '/public/assets/illustration-heart.svg'

import reviewHero from '/public/assets/reviews-hero.png'
import reviewPattern from '/public/assets/pattern-reviews-hero.png'

import reviewsBenefits from '/public/assets/reviews-benefits.png'
import reviewsImagine from '/public/assets/reviews-imagine.png'

import reviewCourtney from '/public/assets/review-courtney.png'
import reviewKatie from '/public/assets/review-katie.png'
import reviewJohn from '/public/assets/review-john.png'
import reviewAndrew from '/public/assets/review-andrew.png'
import { isElementVisible } from 'utils/dom'

export const Reviews = () => {
  const { t } = useTranslation()
  const [hideCTA, setHideCTA] = useState(false)

  const header = useMemo(() => <Header snapToTop />, [])

  useEffect(() => {
    document.addEventListener('scroll', (e) => {
      if (isElementVisible('a.cta')) {
        setHideCTA(true)
      } else {
        setHideCTA(false)
      }
    })
  }, [])

  return (
    <Layout header={header} hideCTA={hideCTA}>
      <ReviewsRoot>
        <HeroSection>
          <Stack
            direction={{ '@bp3': 'row', '@initial': 'column' }}
            gap="7"
            alignItems="center"
            justifyContent="center"
            css={{ '@bp3': { width: '60%' } }}
          >
            <ImageContainer>
              <Image src={isologo} alt="" layout="fill" />
            </ImageContainer>
            <Stack direction="column" css={{ zIndex: 1, position: 'relative' }}>
              <Text
                color="secondary-500"
                weight="medium"
                size={{ '@bp3': 'h1', '@initial': 'h2' }}
                as="h1"
                align={{ '@bp3': 'left', '@initial': 'center' }}
              >
                {t('reviews:title')}
              </Text>
              <Text
                color="secondary-500"
                weight="medium"
                size="body"
                align={{ '@bp3': 'left', '@initial': 'center' }}
                css={{ '@bp1': { fontSize: '$headline' }, marginBottom: '$2' }}
              >
                {t('reviews:subtitle')}
              </Text>
            </Stack>
          </Stack>

          <TrustpilotContent>
            <Trustpilot dataTemplateId="5419b6ffb0d04a076446a9af" theme="dark" />
          </TrustpilotContent>

          <ReviewHero>
            <Image src={reviewHero} alt="" />
          </ReviewHero>
          <ReviewPattern>
            <Image src={reviewPattern} alt="" />
          </ReviewPattern>

          <Masonry>
            <Column warped>
              <ReviewCard>
                <Review
                  name={t('reviews:reviews.stephen.name')}
                  headline={t('reviews:reviews.stephen.headline')}
                  content={t('reviews:reviews.stephen.content')}
                />
              </ReviewCard>

              <ReviewCard color="secondary">
                <InfluencerReview
                  image={reviewKatie}
                  name={t('reviews:reviews.katie.name')}
                  headline={t('reviews:reviews.katie.headline')}
                  content={t('reviews:reviews.katie.content')}
                />
              </ReviewCard>
            </Column>

            <Column>
              <ReviewCard color="secondary">
                <InfluencerReview
                  image={reviewCourtney}
                  name={t('reviews:reviews.courtney.name')}
                  headline={t('reviews:reviews.courtney.headline')}
                  content={t('reviews:reviews.courtney.content')}
                />
              </ReviewCard>

              <ReviewCard color="neutral">
                <Review
                  name={t('reviews:reviews.jenn.name')}
                  headline={t('reviews:reviews.jenn.headline')}
                  content={t('reviews:reviews.jenn.content')}
                />
              </ReviewCard>
            </Column>

            <Column warped>
              <ReviewCard color="neutral">
                <Review
                  name={t('reviews:reviews.megan.name')}
                  headline={t('reviews:reviews.megan.headline')}
                  content={t('reviews:reviews.megan.content')}
                />
              </ReviewCard>

              <ReviewCard>
                <Review
                  name={t('reviews:reviews.manju.name')}
                  headline={t('reviews:reviews.manju.headline')}
                  content={t('reviews:reviews.manju.content')}
                />
              </ReviewCard>
            </Column>
          </Masonry>
        </HeroSection>

        <BenefitsSection>
          <Stack direction="column" alignItems="center">
            <Image src={stepFeed} alt="" width={152} height={152} />
            <Text display="block" size="h2" weight="medium" color="primary-800" align="center">
              {t('reviews:benefits.title')}
            </Text>
          </Stack>

          <BenefitsImage>
            <Image src={reviewsBenefits} alt="" />
          </BenefitsImage>
          <Masonry>
            <Column warped>
              <Box css={{ '@bp3': { height: '460px' }, '@initial': { height: '0px' } }} />
              <ReviewCard color="neutral">
                <Review
                  name={t('reviews:reviews.catherine.name')}
                  headline={t('reviews:reviews.catherine.headline')}
                  content={t('reviews:reviews.catherine.content')}
                />
              </ReviewCard>
              <ReviewCard>
                <Review
                  name={t('reviews:reviews.mikel.name')}
                  headline={t('reviews:reviews.mikel.headline')}
                  content={t('reviews:reviews.mikel.content')}
                />
              </ReviewCard>
              <ReviewCard color="secondary">
                <Review
                  name={t('reviews:reviews.nicole.name')}
                  headline={t('reviews:reviews.nicole.headline')}
                  content={t('reviews:reviews.nicole.content')}
                />
              </ReviewCard>
            </Column>

            <Column>
              <ReviewCard>
                <InfluencerReview
                  image={reviewJohn}
                  name={t('reviews:reviews.john.name')}
                  headline={t('reviews:reviews.john.headline')}
                  content={t('reviews:reviews.john.content')}
                />
              </ReviewCard>

              <Claim>
                <Stack direction="column">
                  <Image src={benefitHeart} alt="" width={96} height={96} />
                  <Text size="h3" weight="medium" color="primary-800" align="center">
                    {t('reviews:benefits.claim')}
                  </Text>
                </Stack>
              </Claim>

              <ReviewCard>
                <Review
                  name={t('reviews:reviews.meghan.name')}
                  headline={t('reviews:reviews.meghan.headline')}
                  content={t('reviews:reviews.meghan.content')}
                />
              </ReviewCard>

              <ReviewCard color="secondary">
                <InfluencerReview
                  image={reviewAndrew}
                  name={t('reviews:reviews.andrew.name')}
                  headline={t('reviews:reviews.andrew.headline')}
                  content={t('reviews:reviews.andrew.content')}
                />
              </ReviewCard>

              <ReviewCard>
                <Review
                  name={t('reviews:reviews.holly.name')}
                  headline={t('reviews:reviews.holly.headline')}
                  content={t('reviews:reviews.holly.content')}
                />
              </ReviewCard>
            </Column>

            <Column warped>
              <ReviewCard color="secondary">
                <Review
                  name={t('reviews:reviews.angela.name')}
                  headline={t('reviews:reviews.angela.headline')}
                  content={t('reviews:reviews.angela.content')}
                />
              </ReviewCard>

              <ReviewCard>
                <Review
                  name={t('reviews:reviews.christina.name')}
                  headline={t('reviews:reviews.christina.headline')}
                  content={t('reviews:reviews.christina.content')}
                />
              </ReviewCard>

              <ReviewCard color="secondary">
                <Review
                  name={t('reviews:reviews.peter.name')}
                  headline={t('reviews:reviews.peter.headline')}
                  content={t('reviews:reviews.peter.content')}
                />
              </ReviewCard>

              <ReviewCard color="neutral">
                <Review
                  name={t('reviews:reviews.janis.name')}
                  headline={t('reviews:reviews.janis.headline')}
                  content={t('reviews:reviews.janis.content')}
                />
              </ReviewCard>
            </Column>
          </Masonry>
        </BenefitsSection>

        <ImagineSection>
          <ImagineSectionContent>
            <Stack
              direction="column"
              gap="5"
              css={{ '@bp3': { maxWidth: '50%' }, '@initial': { maxWidth: 'initial' } }}
            >
              <div>
                <Text display="block" as="h2" size="h2" color="primary-800" weight="medium">
                  {t('reviews:imagine.title1')}
                </Text>
                <Text as="h2" size="h2" color="primary-800" weight="medium">
                  {t('reviews:imagine.title2')}
                </Text>
              </div>

              <Stack direction="column" gap="4">
                <Link href="/meal-plan" passHref>
                  <Button as="a" className="cta">
                    {t('reviews:imagine.cta')}
                  </Button>
                </Link>
                <Link href="/products" passHref>
                  <Text size="headline" as="a" color="primary-500" weight="medium">
                    {t('reviews:imagine.link')}
                  </Text>
                </Link>
              </Stack>
            </Stack>
            <ImagineImage>
              <Image src={reviewsImagine} alt="" />
            </ImagineImage>
          </ImagineSectionContent>
        </ImagineSection>

        <Footer />
      </ReviewsRoot>
    </Layout>
  )
}
